// src/components/AlignersTab.js
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { AlignersPage } from '../pages/AlignersPage';
import {
  Plus,
  Edit3,
  Trash2,
  Activity,
  X,
  Save,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  FileText,
  PauseCircle,
  PlayCircle,
  Sparkles,
  Stethoscope,
  LockKeyhole,
  Filter,
  Search,
  ArrowRight,
} from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const STATUS = { planned:'Planejado', in_progress:'Em andamento', paused:'Pausado', refinement:'Em refinamento', completed:'Concluído', cancelled:'Cancelado' };
const emptyForm = {
  provider: 'SouSmile',

  linked_treatment_id: '',
  commercial_model: 'not_managed',
  commercial_notes: '',

  professional_id: '',
  professional_name: '',
  professional_cro: '',

  status: 'in_progress',
  phase_status: 'follow_up_only',

  start_date: '',
  next_follow_up_date: '',
  purchase_date: '',
  first_evaluation_date: '',
  last_appointment_date: '',

  initial_stages: 0,
  refinement_count: 0,
  refinement_stages: 0,
  total_stages: 0,
  current_stage: 0,

  wear_days_per_aligner: 7,
  current_aligner_started_at: '',
  next_change_date: '',
  expected_end_date: '',

  notes: '',

  next_set_received: false,
  next_set_received_at: '',
  next_set_start_stage: '',
  next_set_end_stage: '',
  all_aligners_delivered: false,
};
const today = () => new Date().toISOString().slice(0,10);

const EVENT_TYPE_CONFIG = {
  set_delivery: {
    label: 'Entrega de conjunto',
    Icon: CheckCircle2,
    cardClass:
      'border-blue-200 bg-blue-50',
    iconClass:
      'bg-blue-100 text-blue-700',
    titleClass:
      'text-blue-900',
  },

  aligner_change: {
    label: 'Troca de alinhador',
    Icon: RefreshCw,
    cardClass:
      'border-purple-200 bg-purple-50',
    iconClass:
      'bg-purple-100 text-purple-700',
    titleClass:
      'text-purple-900',
  },

  follow_up: {
    label: 'Consulta de acompanhamento',
    Icon: Stethoscope,
    cardClass:
      'border-green-200 bg-green-50',
    iconClass:
      'bg-green-100 text-green-700',
    titleClass:
      'text-green-900',
  },

  refinement: {
    label: 'Refinamento',
    Icon: Sparkles,
    cardClass:
      'border-cyan-200 bg-cyan-50',
    iconClass:
      'bg-cyan-100 text-cyan-700',
    titleClass:
      'text-cyan-900',
  },

  intercurrence: {
    label: 'Intercorrência',
    Icon: AlertTriangle,
    cardClass:
      'border-red-200 bg-red-50',
    iconClass:
      'bg-red-100 text-red-700',
    titleClass:
      'text-red-900',
  },

  pause: {
    label: 'Pausa',
    Icon: PauseCircle,
    cardClass:
      'border-amber-200 bg-amber-50',
    iconClass:
      'bg-amber-100 text-amber-700',
    titleClass:
      'text-amber-900',
  },

  resume: {
    label: 'Retomada',
    Icon: PlayCircle,
    cardClass:
      'border-green-200 bg-green-50',
    iconClass:
      'bg-green-100 text-green-700',
    titleClass:
      'text-green-900',
  },

  completed: {
    label: 'Tratamento concluído',
    Icon: CheckCircle2,
    cardClass:
      'border-gray-200 bg-gray-50',
    iconClass:
      'bg-gray-200 text-gray-700',
    titleClass:
      'text-gray-900',
  },

  default: {
    label: 'Evento do tratamento',
    Icon: FileText,
    cardClass:
      'border-gray-200 bg-white',
    iconClass:
      'bg-gray-100 text-gray-600',
    titleClass:
      'text-gray-900',
  },
};

const EVENT_FILTER_OPTIONS = {
  all: 'Todos os eventos',
  automatic: 'Registros automáticos',
  manual: 'Registros manuais',
  set_delivery: 'Entregas de conjunto',
  aligner_change: 'Trocas de alinhador',
  follow_up: 'Consultas de acompanhamento',
  refinement: 'Refinamentos',
  intercurrence: 'Intercorrências',
  pause_resume: 'Pausas e retomadas',
  completed: 'Conclusões',
  clinical_evolution:
    'Registrados na evolução clínica',
};

const formatEventDateBR = (value) => {
  if (!value) {
    return 'Data não informada';
  }

  const normalized = String(value).slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return value;
  }

  const [year, month, day] = normalized.split('-');

  return `${day}/${month}/${year}`;
};

const getEventTypeConfig = (event) =>
  EVENT_TYPE_CONFIG[event?.event_type] ||
  EVENT_TYPE_CONFIG.default;

const isAutomaticEvent = (event) =>
  event?.source === 'automatic' ||
  event?.source === 'system' ||
  event?.event_type === 'set_delivery';

const getDeliveredSetLabel = (event) => {
  if (event?.event_type !== 'set_delivery') {
    return null;
  }

  const titleMatch = String(event?.title || '').match(
    /(\d+)\s+(?:a|até|-)\s+(\d+)/i
  );

  if (titleMatch) {
    return `Alinhadores ${titleMatch[1]} a ${titleMatch[2]}`;
  }

  const descriptionMatch = String(
    event?.description || ''
  ).match(
    /(\d+)\s+(?:a|até|-)\s+(\d+)/i
  );

  if (descriptionMatch) {
    return (
      `Alinhadores ${descriptionMatch[1]} ` +
      `a ${descriptionMatch[2]}`
    );
  }

  if (
    event?.stage_number !== null &&
    event?.stage_number !== undefined
  ) {
    return `Conjunto concluído no alinhador ${event.stage_number}`;
  }

  return 'Conjunto entregue ao paciente';
};

const eventMatchesFilter = (
  event,
  selectedFilter
) => {
  switch (selectedFilter) {
    case 'automatic':
      return isAutomaticEvent(event);

    case 'manual':
      return !isAutomaticEvent(event);

    case 'pause_resume':
      return ['pause', 'resume'].includes(
        event?.event_type
      );

    case 'clinical_evolution':
      return Boolean(
        event?.clinical_evolution_id
      );

    case 'all':
      return true;

    default:
      return (
        event?.event_type === selectedFilter
      );
  }
};

const getEventMonthKey = (value) => {
  const normalized = String(value || '').slice(
    0,
    10
  );

  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(normalized)
  ) {
    return 'without_date';
  }

  return normalized.slice(0, 7);
};

const getEventMonthLabel = (monthKey) => {
  if (monthKey === 'without_date') {
    return 'Sem data informada';
  }

  const [year, month] = monthKey
    .split('-')
    .map(Number);

  const date = new Date(
    year,
    month - 1,
    1
  );

  const formatted = new Intl.DateTimeFormat(
    'pt-BR',
    {
      month: 'long',
      year: 'numeric',
    }
  ).format(date);

  return (
    formatted.charAt(0).toUpperCase() +
    formatted.slice(1)
  );
};

const groupEventsByMonth = (eventList) => {
  const groups = {};

  eventList.forEach((event) => {
    const monthKey = getEventMonthKey(
      event?.event_date
    );

    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }

    groups[monthKey].push(event);
  });

  return Object.entries(groups)
    .sort(([firstKey], [secondKey]) => {
      if (firstKey === 'without_date') {
        return 1;
      }

      if (secondKey === 'without_date') {
        return -1;
      }

      return secondKey.localeCompare(firstKey);
    })
    .map(([monthKey, monthEvents]) => ({
      monthKey,
      label: getEventMonthLabel(monthKey),
      events: monthEvents,
    }));
};

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getEventSearchText = (event) => {
  const config = getEventTypeConfig(event);

  const automatic = isAutomaticEvent(event);

  const evolutionStatus =
    event?.clinical_evolution_id
      ? 'registrado na evolução clínica'
      : 'sem registro na evolução clínica';

  return normalizeSearchText(
    [
      event?.title,
      event?.description,
      event?.professional_name,
      event?.professional_cro,
      event?.event_type,
      config?.label,
      event?.event_date,
      formatEventDateBR(event?.event_date),
      getEventMonthLabel(
        getEventMonthKey(
          event?.event_date
        )
      ),
      event?.stage_number,
      automatic
        ? 'automático automatico sistema'
        : 'manual',
      evolutionStatus,
      getDeliveredSetLabel(event),
    ]
      .filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== ''
      )
      .join(' ')
  );
};

const eventMatchesSearch = (
  event,
  searchValue
) => {
  const normalizedSearch =
    normalizeSearchText(searchValue);

  if (!normalizedSearch) {
    return true;
  }

  const eventSearchText =
    getEventSearchText(event);

  return normalizedSearch
    .split(/\s+/)
    .filter(Boolean)
    .every((term) =>
      eventSearchText.includes(term)
    );
};

export default function AlignersTab({
  selectedPatient,
  toast,
  logAudit,
  onOpenClinicalEvolution,
  focusedAlignerEvent,
  onFocusedAlignerEventHandled,
}) {
  const patientId = selectedPatient?.id;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type':'application/json' });
  const [items, setItems] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing,setEditing]=useState(null); const [showEditor,setShowEditor]=useState(false); const [form,setForm]=useState(emptyForm); const [events,setEvents]=useState({}); const [eventFor,setEventFor]=useState(null);
  const [
    eventFilter,
    setEventFilter,
  ] = useState('all');
  const [
    eventSearch,
    setEventSearch,
  ] = useState('');
  const [
    highlightedAlignerEventId,
    setHighlightedAlignerEventId,
  ] = useState(null);

  const alignerEventRefs =
    useRef({});
  const [eventForm,setEventForm]=useState({event_type:'aligner_change',event_date:today(),stage_number:'',title:'Troca de alinhador',description:'',register_in_clinical_evolution:false});
  const notify=(type,msg)=> toast?.[type]?.(msg);
  const load = async () => {
    if (!patientId) {
      return;
    }

    setLoading(true);

    try {
      /*
      * Antes de carregar, garante que todos os alinhadores
      * deste paciente possuam plano correspondente.
      */
      await axios.post(
        `${API_URL}/aligner-treatments/sync-clinical-plans`,
        {},
        {
          params: {
            patient_id: patientId,
          },
          headers: headers(),
        }
      );

      const [
        alignersResponse,
        professionalsResponse,
      ] = await Promise.all([
        axios.get(
          `${API_URL}/aligner-treatments/patient/${patientId}`,
          {
            headers: headers(),
          }
        ),

        axios.get(
          `${API_URL}/aligner-treatments/professionals`,
          {
            headers: headers(),
          }
        ),
      ]);

      setItems(
        Array.isArray(alignersResponse.data)
          ? alignersResponse.data
          : []
      );

      setProfessionals(
        Array.isArray(professionalsResponse.data)
          ? professionalsResponse.data
          : []
      );
    } catch (error) {
      notify(
        'error',
        error?.response?.data?.detail ||
          'Erro ao carregar tratamentos com alinhadores.'
      );
    } finally {
      setLoading(false);
    }
  };
  const refreshAll = async () => {
    await load();
  };
  useEffect(()=>{load();},[patientId]);
  const openNew=()=>{setEditing(null);setForm({...emptyForm,start_date:today()});setShowEditor(true);};
  const selectProfessional=(id)=>{const p=professionals.find(x=>x.id===id);setForm(v=>({...v,professional_id:id,professional_name:p?.name||'',professional_cro:[p?.cro_number,p?.cro_state].filter(Boolean).join('/') }));};
  const save = async (e) => {
    e.preventDefault();

    const {
      linked_treatment_id,
      ...formWithoutAutomaticLink
    } = form;

    const payload = {
      ...formWithoutAutomaticLink,

      status:
        form.phase_status === 'completed'
          ? 'completed'
          : ['awaiting_documents', 'awaiting_aligners'].includes(form.phase_status)
          ? 'planned'
          : ['awaiting_refinement', 'refinement_active'].includes(form.phase_status)
          ? 'refinement'
          : 'in_progress',

      phase:
        form.phase_status === 'completed'
          ? 'completed'
          : form.phase_status === 'awaiting_retention'
          ? 'finishing'
          : ['awaiting_documents', 'awaiting_aligners'].includes(form.phase_status)
          ? 'preparation'
          : ['awaiting_refinement', 'refinement_active'].includes(form.phase_status)
          ? 'refinement'
          : 'active_treatment',

      next_follow_up_date:
        form.phase_status === 'completed'
          ? null
          : form.next_follow_up_date || null,

      next_set_received:
        !['completed', 'awaiting_retention'].includes(form.phase_status) &&
        form.all_aligners_delivered !== true &&
        form.next_set_received === true,
      next_set_received_at:
        !['completed', 'awaiting_retention'].includes(form.phase_status) &&
        form.all_aligners_delivered !== true &&
        form.next_set_received === true
          ? form.next_set_received_at || null
          : null,

      commercial_model:
        form.commercial_model || 'not_managed',

      commercial_notes:
        form.commercial_notes?.trim() || null,

      patient_id: patientId,
      patient_name: selectedPatient?.full_name || '',
      initial_stages: Number(form.initial_stages || 0),
      refinement_count: Number(form.refinement_count || 0),
      refinement_stages: Number(form.refinement_stages || 0),
      total_stages: Number(form.total_stages || 0),
      next_set_start_stage:
        form.next_set_received && !form.all_aligners_delivered && form.next_set_start_stage !== ''
          ? Number(form.next_set_start_stage)
          : null,
      next_set_end_stage:
        form.next_set_received && !form.all_aligners_delivered && form.next_set_end_stage !== ''
          ? Number(form.next_set_end_stage)
          : null,
      current_stage: Number(form.current_stage || 0),
      wear_days_per_aligner: Number(
        form.wear_days_per_aligner || 7
      ),
    };

    try {
      if (editing) {
        await axios.put(
          `${API_URL}/aligner-treatments/${editing.id}`,
          payload,
          { headers: headers() }
        );
      } else {
        const createdResponse = await axios.post(
          `${API_URL}/aligner-treatments`,
          payload,
          { headers: headers() }
        );

        if (form.notes?.trim() && createdResponse.data?.id) {
          try {
            await axios.post(
              `${API_URL}/aligner-treatments/${createdResponse.data.id}/events`,
              {
                event_type: 'follow_up',
                event_date: form.start_date || today(),
                title: 'Início do acompanhamento',
                description: form.notes.trim(),
                professional_id: form.professional_id || null,
                professional_name: form.professional_name || null,
                register_in_clinical_evolution: true,
                phase_status_after: form.phase_status || 'follow_up_only',
                next_follow_up_date: form.next_follow_up_date || null,
              },
              { headers: headers() }
            );
          } catch (eventError) {
            console.error('Erro ao registrar a evolução inicial do alinhador:', eventError);
            notify(
              'warning',
              'O acompanhamento foi criado, mas a evolução inicial ficou somente nas observações.'
            );
          }
        }
      }

      notify(
        'success',
        editing
          ? 'Tratamento atualizado.'
          : 'Tratamento criado.'
      );

      setEditing(null);
      setForm({ ...emptyForm });
      setShowEditor(false);

      await refreshAll();
    } catch (err) {
      notify(
        'error',
        err?.response?.data?.detail ||
          'Erro ao salvar tratamento.'
      );
    }
  };
  const edit=(t)=>{setEditing(t);setForm({...emptyForm,...t,start_date:t.start_date||today(),phase_status:['awaiting_documents','awaiting_aligners','awaiting_refinement','refinement_active','awaiting_retention','completed'].includes(t.phase_status)?t.phase_status:'follow_up_only',all_aligners_delivered:t.all_aligners_delivered===true||t.phase_status==='final_follow_up'});setShowEditor(true);};
  const remove = async (t) => {
    const confirmed = window.confirm(
      'Inativar este tratamento com alinhadores?'
    );

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/aligner-treatments/${t.id}`,
        { headers: headers() }
      );

      notify('success', 'Tratamento inativado.');

      await refreshAll();
    } catch (err) {
      notify(
        'error',
        err?.response?.data?.detail ||
          'Erro ao inativar tratamento.'
      );
    }
  };
  const loadEvents=async(id)=>{try{const {data}=await axios.get(`${API_URL}/aligner-treatments/${id}/events`,{headers:headers()});setEvents(v=>({...v,[id]:Array.isArray(data)?data:[]}));}catch{notify('error','Erro ao carregar histórico.');}};
  const openEvent = (treatment) => {
    setEventFor(treatment);
    setEventFilter('all');
    setEventSearch('');

    setEventForm({
      event_type: 'aligner_change',
      event_date: today(),
      stage_number: String(
        Math.min(
          Number(
            treatment.current_stage || 0
          ) + 1,
          Number(
            treatment.total_stages || 999
          )
        )
      ),
      title: 'Troca de alinhador',
      description: '',
      register_in_clinical_evolution:
        false,
    });

    loadEvents(treatment.id);
  };

  useEffect(() => {
    if (
      !focusedAlignerEvent?.treatmentId ||
      !focusedAlignerEvent?.eventId
    ) {
      return;
    }

    if (!Array.isArray(items) || !items.length) {
      return;
    }

    const treatmentId = String(
      focusedAlignerEvent.treatmentId
    );

    const linkedTreatment =
      items.find(
        (item) =>
          String(item?.id) ===
          treatmentId
      );

    if (!linkedTreatment) {
      notify(
        'error',
        'O tratamento de alinhadores vinculado não foi localizado.'
      );

      onFocusedAlignerEventHandled?.();
      return;
    }

    setEventFor(linkedTreatment);
    setEventFilter('all');
    setEventSearch('');

    setEventForm({
      event_type: 'aligner_change',
      event_date: today(),
      stage_number: String(
        Math.min(
          Number(
            linkedTreatment.current_stage ||
              0
          ) + 1,
          Number(
            linkedTreatment.total_stages ||
              999
          )
        )
      ),
      title: 'Troca de alinhador',
      description: '',
      register_in_clinical_evolution:
        false,
    });

    loadEvents(linkedTreatment.id);
  }, [
    focusedAlignerEvent,
    items,
    onFocusedAlignerEventHandled,
  ]);

  useEffect(() => {
    if (
      !focusedAlignerEvent?.treatmentId ||
      !focusedAlignerEvent?.eventId ||
      !eventFor?.id
    ) {
      return;
    }

    const treatmentId = String(
      focusedAlignerEvent.treatmentId
    );

    if (
      String(eventFor.id) !== treatmentId
    ) {
      return;
    }

    const eventId = String(
      focusedAlignerEvent.eventId
    );

    const treatmentEvents =
      events[eventFor.id] || [];

    const linkedEvent =
      treatmentEvents.find(
        (event) =>
          String(event?.id) === eventId
      );

    if (!linkedEvent) {
      /*
       * O histórico pode ainda estar sendo carregado.
       * Enquanto não houver eventos, apenas aguardamos.
       */
      if (!treatmentEvents.length) {
        return;
      }

      notify(
        'error',
        'O evento de alinhadores vinculado não foi localizado no histórico.'
      );

      onFocusedAlignerEventHandled?.();
      return;
    }

    let highlightTimer = null;

    const scrollTimer =
      window.setTimeout(() => {
        const target =
          alignerEventRefs.current[
            eventId
          ];

        if (!target) {
          return;
        }

        setHighlightedAlignerEventId(
          eventId
        );

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        highlightTimer =
          window.setTimeout(() => {
            setHighlightedAlignerEventId(
              null
            );
          }, 3500);

        onFocusedAlignerEventHandled?.();
      }, 250);

    return () => {
      window.clearTimeout(scrollTimer);

      if (highlightTimer) {
        window.clearTimeout(
          highlightTimer
        );
      }
    };
  }, [
    focusedAlignerEvent,
    eventFor,
    events,
    onFocusedAlignerEventHandled,
  ]);

  const saveEvent = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API_URL}/aligner-treatments/${eventFor.id}/events`,
        {
          ...eventForm,
          stage_number:
            eventForm.stage_number === ''
              ? null
              : Number(eventForm.stage_number),
        },
        { headers: headers() }
      );

      notify(
        'success',
        eventForm.register_in_clinical_evolution
          ? 'Evento registrado também na evolução clínica.'
          : 'Evento registrado.'
      );

      await loadEvents(eventFor.id);
      await refreshAll();

      setEventForm({
        event_type: 'aligner_change',
        event_date: today(),
        stage_number: String(
          Math.min(
            Number(eventFor.current_stage || 0) + 1,
            Number(eventFor.total_stages || 999)
          )
        ),
        title: 'Troca de alinhador',
        description: '',
        register_in_clinical_evolution: false,
      });
    } catch (err) {
      notify(
        'error',
        err?.response?.data?.detail ||
          'Erro ao registrar evento.'
      );
    }
  };

  const openLinkedClinicalEvolution = (
    event
  ) => {
    if (!event?.clinical_evolution_id) {
      notify(
        'error',
        'Este evento não possui evolução clínica vinculada.'
      );
      return;
    }

    setEventFor(null);

    setHighlightedAlignerEventId(
      null
    );

    alignerEventRefs.current = {};

    if (
      typeof onOpenClinicalEvolution ===
      'function'
    ) {
      onOpenClinicalEvolution(
        event.clinical_evolution_id
      );
    }
  };

  const closeEventModal = () => {
    setEventFor(null);

    setHighlightedAlignerEventId(
      null
    );

    alignerEventRefs.current = {};
  };

  const renderPatientActions = (treatment) => (
    <>
      <button
        type="button"
        onClick={() => edit(treatment)}
        className="
          inline-flex
          items-center
          justify-center
          gap-2
          rounded-xl
          border
          border-gray-300
          bg-white
          px-3
          py-2
          text-sm
          font-semibold
          text-gray-700
          hover:bg-gray-50
        "
      >
        <Edit3 size={16} />
        Editar
      </button>

      <button
        type="button"
        onClick={() => remove(treatment)}
        className="
          inline-flex
          items-center
          justify-center
          gap-2
          rounded-xl
          border
          border-red-200
          bg-white
          px-3
          py-2
          text-sm
          font-semibold
          text-red-600
          hover:bg-red-50
        "
      >
        <Trash2 size={16} />
        Inativar
      </button>
    </>
  );

  const currentTreatmentEvents =
    eventFor
      ? events[eventFor.id] || []
      : [];

  const filteredTreatmentEvents =
    currentTreatmentEvents
      .filter(
        (event) =>
          eventMatchesFilter(
            event,
            eventFilter
          ) &&
          eventMatchesSearch(
            event,
            eventSearch
          )
      )
      .sort((firstEvent, secondEvent) => {
        const firstDate =
          firstEvent?.event_date || '';

        const secondDate =
          secondEvent?.event_date || '';

        if (firstDate !== secondDate) {
          return secondDate.localeCompare(
            firstDate
          );
        }

        return String(
          secondEvent?.created_at || ''
        ).localeCompare(
          String(
            firstEvent?.created_at || ''
          )
        );
      });

  const groupedTreatmentEvents =
    groupEventsByMonth(
      filteredTreatmentEvents
    );

  const hasActiveEventFilters =
    eventFilter !== 'all' ||
    Boolean(eventSearch.trim());

  const clearEventFilters = () => {
    setEventFilter('all');
    setEventSearch('');
  };

  return <div className="space-y-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className="text-lg font-semibold">Acompanhamento de alinhadores</h3>
        <p className="text-sm text-gray-500">Registre somente o necessário para acompanhar o paciente no OdontoAssist.</p>
      </div>
      <button onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white">
        <Plus size={16}/>Iniciar acompanhamento
      </button>
    </div>

    {showEditor && (
      <form onSubmit={save} className="space-y-4 rounded-xl border border-purple-200 bg-purple-50/40 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <strong>{editing ? 'Editar acompanhamento' : 'Iniciar acompanhamento'}</strong>
            <p className="mt-1 text-xs text-gray-500">Não é necessário repetir os dados já controlados pela SouSmile.</p>
          </div>
          <button type="button" onClick={()=>{setEditing(null);setForm({...emptyForm});setShowEditor(false)}}><X size={18}/></button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            Data do acompanhamento
            <input type="date" required className="mt-1 w-full rounded border p-2" value={form.start_date||''} onChange={e=>setForm({...form,start_date:e.target.value})}/>
          </label>

          <label className="text-sm">
            Lembrar de falar com o paciente em <span className="text-gray-400">(opcional)</span>
            <input type="date" className="mt-1 w-full rounded border p-2" value={form.next_follow_up_date||''} onChange={e=>setForm({...form,next_follow_up_date:e.target.value})}/>
          </label>

          <label className="text-sm md:col-span-2">
            Situação
            <select className="mt-1 w-full rounded border p-2" value={form.phase_status||'follow_up_only'} onChange={e=>setForm({...form,phase_status:e.target.value})}>
              <option value="awaiting_documents">Aguardando documentação/exames</option>
              <option value="awaiting_aligners">Aguardando alinhadores</option>
              <option value="follow_up_only">Em acompanhamento</option>
              <option value="awaiting_refinement">Aguardando refinamento / novos alinhadores</option>
              <option value="refinement_active">Em refinamento</option>
              <option value="awaiting_retention">Aguardando contenção</option>
              <option value="completed">Finalizado</option>
            </select>
          </label>
        </div>

        <label className="block text-sm">
          {editing ? 'Observações' : 'Evolução inicial'} <span className="text-gray-400">(opcional)</span>
          <textarea className="mt-1 w-full rounded border p-2" rows="4" value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Escreva somente o que for importante sobre este acompanhamento."/>
        </label>

        {!['completed', 'awaiting_retention'].includes(form.phase_status) && form.all_aligners_delivered !== true && <section className="rounded-xl border border-blue-200 bg-blue-50 p-3">
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={form.next_set_received===true} onChange={e=>setForm({...form,next_set_received:e.target.checked,next_set_received_at:e.target.checked?(form.next_set_received_at||today()):''})}/>
            <span>
              <span className="block text-sm font-semibold text-blue-900">Os próximos alinhadores chegaram à clínica</span>
              <span className="mt-1 block text-xs text-blue-700">Use como controle interno do material que já está na clínica.</span>
            </span>
          </label>
          {form.next_set_received===true && (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="text-sm text-blue-900">
                Alinhadores recebidos
                <div className="mt-1 flex items-center gap-2">
                  <input type="number" min="1" required placeholder="Do" className="w-full rounded border border-blue-200 bg-white p-2" value={form.next_set_start_stage||''} onChange={e=>setForm({...form,next_set_start_stage:e.target.value})}/>
                  <span>até</span>
                  <input type="number" min="1" required placeholder="Até" className="w-full rounded border border-blue-200 bg-white p-2" value={form.next_set_end_stage||''} onChange={e=>setForm({...form,next_set_end_stage:e.target.value})}/>
                </div>
              </label>
              <label className="text-sm text-blue-900">
                Total previsto no tratamento
                <input type="number" min="1" placeholder="Ex.: 20" className="mt-1 w-full rounded border border-blue-200 bg-white p-2" value={form.total_stages||''} onChange={e=>setForm({...form,total_stages:e.target.value})}/>
              </label>
              <label className="text-sm text-blue-900 md:col-span-2">
                Data da chegada
                <input type="date" className="mt-1 w-full rounded border border-blue-200 bg-white p-2 md:w-64" value={form.next_set_received_at||''} onChange={e=>setForm({...form,next_set_received_at:e.target.value})}/>
              </label>
            </div>
          )}
        </section>}

        {form.phase_status !== 'completed' && <label className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={form.all_aligners_delivered===true} onChange={e=>setForm(current=>({...current,all_aligners_delivered:e.target.checked,next_set_received:e.target.checked?false:current.next_set_received,next_set_received_at:e.target.checked?'':current.next_set_received_at,next_set_start_stage:e.target.checked?'':current.next_set_start_stage,next_set_end_stage:e.target.checked?'':current.next_set_end_stage}))}/>
          <span>
            <span className="block text-sm font-semibold text-green-900">Todos os alinhadores já foram entregues ao paciente</span>
            <span className="mt-1 block text-xs text-green-700">Pode ser marcado junto com qualquer situação clínica, inclusive “Em refinamento”.</span>
          </span>
        </label>}

        <button className="inline-flex items-center gap-2 rounded bg-purple-600 px-4 py-2 text-white"><Save size={16}/>{editing?'Salvar alterações':'Iniciar acompanhamento'}</button>
      </form>
    )}
    <AlignersPage
      embedded
      patientId={patientId}
      renderExtraActions={renderPatientActions}
      onDataUpdated={load}
    />
    {eventFor && (
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
        onMouseDown={closeEventModal}
      >
        <div
          className="mt-8 w-full max-w-2xl rounded-xl bg-white p-5"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="mb-4 flex justify-between">
            <h3 className="font-semibold">
              Histórico — {eventFor.provider}
            </h3>

            <button
              type="button"
              onClick={closeEventModal}
            >
              <X />
            </button>
          </div>

          <form
            onSubmit={saveEvent}
            className="grid gap-3 rounded border bg-gray-50 p-3 sm:grid-cols-2"
          >
            <label className="text-sm">
              Tipo

              <select
                className="mt-1 w-full rounded border p-2"
                value={eventForm.event_type}
                onChange={(event) =>
                  setEventForm({
                    ...eventForm,
                    event_type: event.target.value,
                    title:
                      event.target.options[
                        event.target.selectedIndex
                      ].text,
                  })
                }
              >
                <option value="aligner_change">
                  Troca de alinhador
                </option>

                <option value="follow_up">
                  Consulta de acompanhamento
                </option>

                <option value="refinement">
                  Refinamento iniciado
                </option>

                <option value="intercurrence">
                  Intercorrência
                </option>

                <option value="pause">
                  Pausa
                </option>

                <option value="resume">
                  Retomada
                </option>

                <option value="completed">
                  Tratamento concluído
                </option>
              </select>
            </label>

            <label className="text-sm">
              Data

              <input
                type="date"
                className="mt-1 w-full rounded border p-2"
                value={eventForm.event_date}
                onChange={(event) =>
                  setEventForm({
                    ...eventForm,
                    event_date: event.target.value,
                  })
                }
              />
            </label>

            <label className="text-sm">
              Etapa

              <input
                type="number"
                min="0"
                className="mt-1 w-full rounded border p-2"
                value={eventForm.stage_number}
                onChange={(event) =>
                  setEventForm({
                    ...eventForm,
                    stage_number: event.target.value,
                  })
                }
              />
            </label>

            <label className="text-sm sm:col-span-2">
              Descrição

              <textarea
                className="mt-1 w-full rounded border p-2"
                value={eventForm.description}
                onChange={(event) =>
                  setEventForm({
                    ...eventForm,
                    description: event.target.value,
                  })
                }
              />
            </label>

            <label className="flex items-start gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3 sm:col-span-2">
              <input
                type="checkbox"
                checked={
                  eventForm.register_in_clinical_evolution
                }
                onChange={(event) =>
                  setEventForm({
                    ...eventForm,
                    register_in_clinical_evolution:
                      event.target.checked,
                  })
                }
                className="mt-1 h-4 w-4"
              />

              <span>
                <span className="block text-sm font-semibold text-purple-900">
                  Registrar também na evolução clínica
                </span>

                <span className="mt-1 block text-xs text-purple-700">
                  Este evento será incluído no histórico clínico
                  do paciente.
                </span>
              </span>
            </label>

            <button
              type="submit"
              className="rounded bg-purple-600 px-4 py-2 text-white sm:col-span-2"
            >
              Registrar evento
            </button>
          </form>

          <div className="mt-6">
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h4 className="font-semibold text-gray-900">
                  Histórico do tratamento
                </h4>

                <p className="mt-0.5 text-xs text-gray-500">
                  Eventos clínicos e operacionais
                  registrados neste acompanhamento.
                </p>
              </div>

              <div className="min-w-0 w-full flex flex-col gap-2 lg:w-auto lg:max-w-full lg:items-end">
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
                  <label className="relative">
                    <span className="sr-only">
                      Pesquisar no histórico
                    </span>

                    <Search
                      size={15}
                      className="
                        pointer-events-none
                        absolute
                        left-3
                        top-1/2
                        -translate-y-1/2
                        text-gray-400
                      "
                    />

                    <input
                      type="search"
                      value={eventSearch}
                      onChange={(event) =>
                        setEventSearch(
                          event.target.value
                        )
                      }
                      placeholder="Pesquisar histórico"
                      className="
                        w-full
                        rounded-xl
                        border
                        border-gray-300
                        bg-white
                        py-2
                        pl-9
                        pr-3
                        text-sm
                        text-gray-700
                        outline-none
                        focus:border-purple-400
                        sm:w-48
                        lg:w-52
                      "
                    />
                  </label>

                  <label className="relative">
                  <span className="sr-only">
                    Filtrar histórico
                  </span>

                  <Filter
                    size={15}
                    className="
                      pointer-events-none
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      text-gray-400
                    "
                  />

                  <select
                    value={eventFilter}
                    onChange={(event) =>
                      setEventFilter(
                        event.target.value
                      )
                    }
                    className="
                      w-full
                      rounded-xl
                      border
                      border-gray-300
                      bg-white
                      py-2
                      pl-9
                      pr-8
                      text-sm
                      text-gray-700
                      outline-none
                      focus:border-purple-400
                      max-w-full
                      sm:w-auto
                    "
                  >
                    {Object.entries(
                      EVENT_FILTER_OPTIONS
                    ).map(
                      ([value, label]) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </label>

                  <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-center text-xs font-semibold text-gray-600">
                    {
                      filteredTreatmentEvents.length
                    }{' '}
                    {filteredTreatmentEvents.length ===
                    1
                      ? 'evento'
                      : 'eventos'}
                  </span>
                </div>

                {hasActiveEventFilters && (
                  <button
                    type="button"
                    onClick={clearEventFilters}
                    className="
                      inline-flex
                      max-w-full
                      items-center
                      gap-1
                      self-start
                      whitespace-normal
                      text-left
                      text-xs
                      font-semibold
                      text-purple-700
                      hover:text-purple-900
                      lg:self-end
                    "
                  >
                    <X size={13} />
                    Limpar pesquisa e filtros
                  </button>
                )}
              </div>
            </div>

            {currentTreatmentEvents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center">
                <Activity
                  size={24}
                  className="mx-auto text-gray-400"
                />

                <p className="mt-2 text-sm font-medium text-gray-700">
                  Nenhum evento registrado
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Utilize o formulário acima para inserir o
                  primeiro registro deste tratamento.
                </p>
              </div>
            ) : filteredTreatmentEvents.length ===
              0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center">
                <Filter
                  size={24}
                  className="mx-auto text-gray-400"
                />

                <p className="mt-2 text-sm font-medium text-gray-700">
                  Nenhum evento encontrado
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Existem registros no histórico,
                  mas nenhum corresponde à pesquisa
                  e aos filtros selecionados.
                </p>

                <button
                  type="button"
                  onClick={clearEventFilters}
                  className="
                    mt-3
                    rounded-lg
                    border
                    border-gray-300
                    bg-white
                    px-3
                    py-1.5
                    text-xs
                    font-semibold
                    text-gray-700
                    hover:bg-gray-100
                  "
                >
                  Limpar pesquisa e filtros
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedTreatmentEvents.map(
                  (monthGroup) => (
                    <section
                      key={monthGroup.monthKey}
                    >
                      <div className="mb-3 flex items-center gap-3">
                        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                          {monthGroup.label}
                        </span>

                        <div className="h-px flex-1 bg-gray-200" />
                      </div>

                      <div className="relative space-y-4 pl-7">
                        <div
                          className="
                            absolute
                            bottom-3
                            left-[11px]
                            top-3
                            w-px
                            bg-gray-200
                          "
                        />

                        {monthGroup.events.map(
                          (event) => {
                            const config =
                              getEventTypeConfig(event);

                            const EventIcon =
                              config.Icon;

                            const automatic =
                              isAutomaticEvent(event);

                            const deliveredSetLabel =
                              getDeliveredSetLabel(
                                event
                              );

                            const eventId =
                              String(event.id);

                            const isHighlighted =
                              String(
                                highlightedAlignerEventId ||
                                  ''
                              ) === eventId;

                            return (
                              <div
                                key={event.id}
                                ref={(element) => {
                                  if (element) {
                                    alignerEventRefs.current[
                                      eventId
                                    ] = element;
                                  } else {
                                    delete alignerEventRefs.current[
                                      eventId
                                    ];
                                  }
                                }}
                                className={`
                                  relative
                                  rounded-2xl
                                  pl-8
                                  transition-all
                                  duration-500
                                  ${
                                    isHighlighted
                                      ? 'bg-purple-100/70 shadow-lg ring-4 ring-purple-300'
                                      : ''
                                  }
                                `}
                              >
                                <div
                                  className="
                                    absolute
                                    -left-[22px]
                                    top-5
                                    z-10
                                    h-3
                                    w-3
                                    rounded-full
                                    border-2
                                    border-white
                                    bg-purple-500
                                    shadow
                                  "
                                />

                                <article
                                  className={`
                                    rounded-xl
                                    border
                                    p-4
                                    ${config.cardClass}
                                  `}
                                >
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={`
                                        flex
                                        h-10
                                        w-10
                                        shrink-0
                                        items-center
                                        justify-center
                                        rounded-full
                                        ${config.iconClass}
                                      `}
                                    >
                                      <EventIcon
                                        size={19}
                                      />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                          <div className="flex flex-wrap items-center gap-2">
                                            <h5
                                              className={`
                                                font-semibold
                                                ${config.titleClass}
                                              `}
                                            >
                                              {event.title ||
                                                config.label}
                                            </h5>

                                            <span
                                              className={`
                                                rounded-full
                                                px-2
                                                py-0.5
                                                text-[10px]
                                                font-bold
                                                uppercase
                                                tracking-wide
                                                ${
                                                  automatic
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-white/80 text-gray-600'
                                                }
                                              `}
                                            >
                                              {automatic
                                                ? 'Automático'
                                                : 'Manual'}
                                            </span>
                                          </div>

                                          <p className="mt-1 text-xs text-gray-500">
                                            {formatEventDateBR(
                                              event.event_date
                                            )}
                                          </p>
                                        </div>

                                        {automatic && (
                                          <div className="inline-flex items-center gap-1 rounded-lg bg-white/70 px-2 py-1 text-[11px] font-medium text-gray-600">
                                            <LockKeyhole
                                              size={12}
                                            />
                                            Somente leitura
                                          </div>
                                        )}
                                      </div>

                                      {deliveredSetLabel && (
                                        <div className="mt-3 inline-flex rounded-lg border border-blue-200 bg-white/70 px-3 py-2 text-sm font-semibold text-blue-800">
                                          {
                                            deliveredSetLabel
                                          }
                                        </div>
                                      )}

                                      {event.stage_number !==
                                        null &&
                                        event.stage_number !==
                                          undefined &&
                                        event.event_type !==
                                          'set_delivery' && (
                                          <p className="mt-2 text-sm text-gray-700">
                                            Etapa/alinhador:{' '}
                                            <strong>
                                              {
                                                event.stage_number
                                              }
                                            </strong>
                                          </p>
                                        )}

                                      {event.description && (
                                        <div className="mt-3 whitespace-pre-line rounded-lg bg-white/70 p-3 text-sm leading-relaxed text-gray-700">
                                          {
                                            event.description
                                          }
                                        </div>
                                      )}

                                      {event.professional_name && (
                                        <p className="mt-3 text-xs text-gray-600">
                                          <strong>
                                            Profissional:
                                          </strong>{' '}
                                          {
                                            event.professional_name
                                          }
                                        </p>
                                      )}

                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {event.clinical_evolution_id ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              openLinkedClinicalEvolution(
                                                event
                                              )
                                            }
                                            className="
                                              inline-flex
                                              items-center
                                              gap-1.5
                                              rounded-full
                                              bg-green-100
                                              px-2.5
                                              py-1
                                              text-xs
                                              font-semibold
                                              text-green-700
                                              transition
                                              hover:bg-green-200
                                              hover:text-green-900
                                              focus:outline-none
                                              focus:ring-2
                                              focus:ring-green-400
                                              focus:ring-offset-1
                                            "
                                            title="Abrir este registro na aba Evolução Clínica"
                                          >
                                            <CheckCircle2
                                              size={13}
                                            />

                                            Ver evolução clínica

                                            <ArrowRight
                                              size={13}
                                            />
                                          </button>
                                        ) : (
                                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                                            Sem registro na
                                            evolução clínica
                                          </span>
                                        )}

                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-2.5 py-1 text-xs font-medium text-gray-600">
                                          {config.label}
                                        </span>
                                      </div>

                                      {automatic && (
                                        <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
                                          Este registro foi
                                          gerado pelo sistema a
                                          partir de uma operação
                                          do tratamento e não
                                          pode ser alterado
                                          diretamente.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </article>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </section>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>;
}
